<template>
  <b-row class="profile-form-container has-box-shadow">
    <b-container class="p-0 position-relative">
      <b-row no-gutters class="has-border pb-3 mb-3">
        <b-col class="d-flex justify-content-between">
          <h4 class="header">{{ $t('myAccount.paymentOptions') }}</h4>
          <b-link v-if="hasData" class="link-warning" @click="isEditMode = !isEditMode">{{
            isEditMode ? $t('base.cancel') : $t('myAccount.edit')
          }}</b-link>
        </b-col>
      </b-row>

      <p class="description">{{ $t('myAccount.paymentOptionsDesc') }}</p>

      <b-row class="mb-2">
        <b-col cols="12" class="font-weight-bold">{{ $t('myAccount.yourCreditCards') }}</b-col>
      </b-row>

      <b-row v-if="!isFetching && !hasData" class="mt-3">
        <b-col class="gray-font">{{ $t('myAccount.notRegistered') }}</b-col>
      </b-row>

      <div v-if="isFetching" class="d-flex justify-content-center mt-3">
        <b-spinner></b-spinner>
      </div>

      <b-row v-if="hasData" class="credit-card-row">
        <b-col v-for="cc in creditCards" :key="cc.id" cols="12" sm="6" md="4" xl="3">
          <registered-credit-card
            class="credit-card h-100"
            :card="cc"
            :is-available="isCardAvailable(cc)"
          >
            <template #action>
              <b-button
                v-if="isEditMode"
                variant="outline-dark"
                class="mt-3 minw-2"
                @click="onRemove(cc)"
                >{{ $t('base.remove') }}</b-button
              >
            </template>
          </registered-credit-card>
        </b-col>
      </b-row>

      <b-modal
        id="bv-modal-remove-cc"
        hide-footer
        centered
        :title="$t('myAccount.removeCreditCard')"
        @hidden="hideModal"
      >
        <p>{{ $t('myAccount.removeCreditCardConfirm') }}</p>

        <registered-credit-card
          v-if="selectedCreditCard"
          class="credit-card"
          :card="selectedCreditCard"
        ></registered-credit-card>
        <form-error class="mt-3" :errors="deleteErrors" />
        <div class="d-flex mt-3">
          <b-button
            class="w-100 mr-2"
            variant="outline-secondary"
            :disabled="isDeleting"
            @click="onCancel"
            >{{ $t('base.cancel') }}</b-button
          >
          <b-button
            class="w-100"
            variant="primary-dark"
            :disabled="isDeleting"
            @click="handleRemove"
          >
            <b-spinner v-show="isDeleting" class="mr-2" small></b-spinner>
            {{ $t('base.remove') }}
          </b-button>
        </div>
      </b-modal>
    </b-container>
  </b-row>
</template>

<script>
import RegisteredCreditCard from 'components/RegisteredCreditCards/RegisteredCreditCard'
import MembershipApi from 'api/Membership'
import FormError from 'components/FormError'
import { mapGetters } from 'vuex'
import { ThirdPartyPayment } from 'constants/tripla'
import {
  prepareCreditCards,
  isCardOKForGMOPaypal,
  isCardOKForPaygentPaypal,
  isCardOKForGMO,
  isCardOKForPaygent,
  isCardOKForAdyenGMO,
  isCardOKForAdyenGMOPaypal
} from 'utilities/payment/thirdPartyPayment'

export default {
  name: 'MyProfileFormPaymentOptions',
  components: { RegisteredCreditCard, FormError },
  data() {
    return {
      isEditMode: false,
      isFetching: false,
      isDeleting: false,
      creditCards: [],
      selectedCreditCard: undefined,
      deleteErrors: null,
      isPaymentGatewayUnderTransition: false
    }
  },

  computed: {
    ...mapGetters({
      isMcpMode: 'mcp/isMcpMode',
      initialSetting: 'setting/getInit',
      nonMcpPaymentGatewaySetting: 'setting/nonMcpPaymentGatewaySetting',
      paymentGatewaySetting: 'setting/getPaymentGatewaySetting'
    }),
    hasData() {
      return !this.isFetching && this.creditCards?.length
    },
    isPontaVisaSupport() {
      return (process.env.PONTA_VISA_CARD_BRANDS || '')
        .split(',')
        .includes(String(this.initialSetting?.hotel_brand_id))
    }
  },

  created() {
    this.fetchPaymentOptions()
  },

  methods: {
    fetchPaymentOptions() {
      this.isFetching = true
      MembershipApi.getCreditCards()
        .then((res) => {
          this.creditCards = prepareCreditCards(res.data)
          this.isPaymentGatewayUnderTransition =
            this.nonMcpPaymentGatewaySetting.name !== res.payment_gateway
          this.isFetching = false
        })
        .catch(() => {
          this.isFetching = false
        })
    },

    isCardAvailable(card) {
      if (this.isPaymentGatewayUnderTransition) {
        return false
      }

      if (
        card.hasOwnProperty('gmo_account') &&
        card.gateway === this.nonMcpPaymentGatewaySetting.name
      ) {
        if (card.gmo_account !== this.nonMcpPaymentGatewaySetting.gmo_account) {
          return false
        }
      }

      switch (this.nonMcpPaymentGatewaySetting.name) {
        case ThirdPartyPayment.Paygent:
          return isCardOKForPaygent(card)
        case ThirdPartyPayment.GMO:
          return isCardOKForGMO(card)
        case ThirdPartyPayment.GMOPayPal:
          return isCardOKForGMOPaypal(card)
        case ThirdPartyPayment.PaygentPayPal:
          return isCardOKForPaygentPaypal(card)
        case ThirdPartyPayment.AdyenGMO:
          return isCardOKForAdyenGMO(card)
        case ThirdPartyPayment.AdyenGMOPayPal:
          return isCardOKForAdyenGMOPaypal(card, this.isPontaVisaSupport)
        default:
          return true
      }
    },

    handleRemove() {
      this.isDeleting = true
      let payload = null
      if (
        [
          ThirdPartyPayment.GMOPayPal,
          ThirdPartyPayment.PaygentPayPal,
          ThirdPartyPayment.GMO,
          ThirdPartyPayment.Paygent,
          ThirdPartyPayment.AdyenGMO,
          ThirdPartyPayment.AdyenGMOPayPal
        ].includes(this.nonMcpPaymentGatewaySetting?.name)
      ) {
        payload = {
          gateway: this.selectedCreditCard.gateway,
          gmo_account: this.selectedCreditCard.gmo_account
        }
      }

      MembershipApi.deleteCreditCard(this.selectedCreditCard.sequence, payload)
        .then(() => {
          this.onCancel()
          this.fetchPaymentOptions()
        })
        .catch((err) => {
          this.deleteErrors = err.titleList
        })
        .finally(() => {
          this.isDeleting = false
        })
    },

    onRemove(cc) {
      this.selectedCreditCard = cc
      this.$bvModal.show('bv-modal-remove-cc')
    },

    onCancel() {
      this.$bvModal.hide('bv-modal-remove-cc')
    },

    hideModal() {
      this.selectedCreditCard = undefined
      this.deleteErrors = null
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~stylesheets/vars.scss';
@import '~stylesheets/mixins.scss';

.has-box-shadow {
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
}

.has-border {
  border-bottom: 2px solid $light-gray;
}

.credit-card-row {
  margin: -8px;

  [class*='col-'] {
    padding: 8px;
  }
}

.credit-card {
  padding: 15px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.2);
}

.alert-danger {
  border-left-width: 14px;
  padding: 10px 5px 10px 10px;
}
</style>
